import {
  SignalStoreFeature,
  SignalStoreFeatureResult,
  EmptyFeatureResult,
  StateSignals,
  signalStoreFeature,
  withMethods,
  patchState,
  withHooks,
} from '@ngrx/signals';
import { effect } from '@angular/core';

export const withLocalStorage = <
  Input extends SignalStoreFeatureResult,
  StateProp extends keyof StateSignals<Input['state']>,
>(
  namespace: string,
  stateProp: StateProp,
  storageKey = stateProp.toString(),
): SignalStoreFeature<Input, EmptyFeatureResult> => {
  // Method names are derived from `stateProp` (not fixed `save`/`loadFromLocalStorage`
  // names) so this feature can be composed more than once in the same store — e.g. a
  // store persisting both a wizard's step-1 form and step-3 form under separate keys.
  // Fixed names would collide: the second registration's withMethods would overwrite
  // the first's on the composed store, so the first feature's onInit hook (which reads
  // `s.loadFromLocalStorage` by that same name at hook-invocation time) would silently
  // run the SECOND feature's load/save logic against its own key instead.
  const propName = stateProp.toString();
  const suffix = propName.charAt(0).toUpperCase() + propName.slice(1);
  const saveMethodName = `save${suffix}ToLocalStorage`;
  const loadMethodName = `load${suffix}FromLocalStorage`;

  return signalStoreFeature(
    withMethods(s => {
      const key = `${namespace}_${storageKey}`;

      return {
        [saveMethodName]: <T>(state: T) => localStorage.setItem(key, JSON.stringify(state)),
        [loadMethodName]: () => {
          try {
            const v = localStorage.getItem(key);

            v && patchState(s, { [stateProp]: JSON.parse(v) });
          } catch {
            console.error(`Couldn't load '${key}' from local storage`);
          }
        },
      };
    }),
    withHooks({
      onInit: s => {
        (s as unknown as Record<string, () => void>)[loadMethodName]();
        effect(() =>
          (s as unknown as Record<string, <T>(state: T) => void>)[saveMethodName](
            (s as unknown as Record<string, () => unknown>)[propName](),
          ),
        );
      },
    }),
  );
};
